/**
 * Estimator screen (WO-25).
 *
 * Wraps EstimatorPane with the standard screen chrome.
 *
 * tenancyUnitId: falls back to session user's organizationId.
 * ASSUMPTION — same as AssistantPane: confirm whether jobId / estimateId
 * is the intended tenancyUnitId (needed for estimatorActivate to associate
 * the draft with the right BEAM estimate record). See EstimatorPane module doc.
 *
 * On activation the pane calls onActivated(estimateId): the backend has materialized
 * a draft BEAM estimate from the draft's line items (design doc §4/§9), so the screen
 * navigates straight to the native estimate detail view.
 *
 * AUTHORITY GATE (CLAUDE.md invariant 6 / master spec §4):
 *   Staff role may reach this screen and draft/present estimates.
 *   No "Approve", "Send for Signature", or "Lock" actions are present here.
 *   Those are owner-only steps in the BEAM estimate lifecycle and are enforced
 *   server-side (WO-16 approval gate).
 *
 * NEVER imports @smartbox/* (mobile CLAUDE.md invariant 1).
 */

import { StyleSheet, View } from 'react-native';
import { useRouter } from 'expo-router';
import { useSession } from '../../src/auth/session';
import { Screen, Text } from '../../src/components/ui';
import { EstimatorPane } from '../../src/components/estimator/EstimatorPane';
import { theme } from '../../src/theme';

export default function EstimatorScreen() {
  const router = useRouter();
  const user = useSession((s) => s.user);

  /**
   * ASSUMPTION: organizationId used as tenancyUnitId.
   * Replace with a contextual jobId / estimateId once those are available
   * in mobile navigation params (WO-M4).
   */
  const tenancyUnitId = user?.organizationId ?? '';

  if (!tenancyUnitId) {
    return (
      <Screen padded>
        <View style={styles.centeredState} accessibilityRole="alert">
          <Text variant="body" color="danger" style={styles.centered}>
            Session not ready. Please go back and try again.
          </Text>
        </View>
      </Screen>
    );
  }

  function handleActivated(estimateId: string) {
    // The backend materialized a draft BEAM estimate from the draft's line items
    // (design doc §4/§9). Navigate to its native detail view so the owner can review,
    // price, approve, and send-for-signature (all owner-gated, server-enforced).
    router.push({ pathname: '/(app)/estimates/[id]', params: { id: estimateId } });
  }

  // The Stack header (title "Estimate Draft" + back) is provided by the (app) navigator —
  // no in-screen header here (that caused a duplicate header bar).
  return (
    <Screen padded={false} edges={['left', 'right', 'bottom']}>
      <EstimatorPane
        tenancyUnitId={tenancyUnitId}
        onActivated={handleActivated}
      />
    </Screen>
  );
}

const styles = StyleSheet.create({
  centeredState: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: theme.spacing['3xl'],
  },
  centered: {
    textAlign: 'center',
  },
});
